/**
 * Common English words that should never be corrected by veterinary terminology
 * Centralized list used across all terminology processing
 */
export const COMMON_WORDS = new Set([
  // Articles, prepositions, conjunctions
  'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
  'from', 'into', 'over', 'after', 'under', 'above', 'below', 'through', 'without',
  
  // Verbs
  'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did',
  'will', 'would', 'could', 'should', 'can', 'may', 'might', 'must', 'said',
  
  // Pronouns
  'a', 'an', 'this', 'that', 'these', 'those', 'my', 'your', 'his', 'her', 'its', 'our', 'their',
  'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them',
  
  // Question words
  'who', 'what', 'where', 'when', 'why', 'how', 'which', 'whose', 'while',
  
  // Quantifiers and negation
  'not', 'no', 'yes', 'all', 'any', 'some', 'many', 'much', 'more', 'most', 'less', 'few',
  
  // Numbers and order
  'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'first', 'second', 'third', 'last', 'next', 'previous', 'each',
  
  // Common adjectives
  'good', 'bad', 'big', 'small', 'old', 'new', 'long', 'short', 'high', 'low',
  
  // Veterinary context words that should not be corrected
  'owner', 'reports', 'that', 'bella', 'year', 'days', 'she', 'still', 'eating',
  'drinking', 'normally', 'her', 'activity', 'level', 'been', 'little', 'lower', 'than', 'usual',
  'today', 'about', 'bright', 'alert', 'responsive', 'normal', 'regular', 'less',
  'sounds', 'look', 'good', 'both', 'assessment', 'would', 'include', 'prognosis',
  'care', 'plan', 'ordering', 'treatment', 'medication', 'also', 'fresh', 'water',
  'recheck', 'days', 'sooner', 'over', 'goes', 'worsens', 'appetite', 'drops', 'should',
  'monitor', 'similar', 'signs'
]);

/**
 * Check if a word is a common English word that should not be corrected
 */
export function isCommonWord(word: string): boolean {
  return COMMON_WORDS.has(word.toLowerCase().trim());
}

/**
 * Check if a word is likely to be a veterinary term that needs correction
 */
export function isLikelyVeterinaryTerm(word: string): boolean {
  if (isCommonWord(word)) {
    return false;
  }
  
  // Look for patterns that suggest veterinary terms
  const veterinaryPatterns = [
    /.*ine$/, // medications ending in -ine (banamine, acepromazine)
    /.*zole$/, // medications ending in -zole (omeprazole)
    /.*mycin$/, // antibiotics ending in -mycin (gentamicin)
    /.*cillin$/, // antibiotics ending in -cillin (penicillin)
    /.*sone$/, // steroids ending in -sone (dexamethasone)
    /.*itis$/, // conditions ending in -itis (laminitis)
    /.*scopy$/, // procedures ending in -scopy (gastroscopy)
    /.*graph/, // imaging terms (radiograph)
    /.*sound/, // ultrasound
    /bcs?$/, // body condition score
    /cbc$/, // complete blood count
    /pcr$/, // PCR test
    /uri$/, // upper respiratory infection
  ];
  
  return veterinaryPatterns.some(pattern => pattern.test(word.toLowerCase()));
}
